/**
 * Candado de sesión: cuando la identidad deja de ser fiable, no se envía nada.
 *
 * `localStorage` es compartido por todas las pestañas del mismo origen. El
 * 16-ago-2026, entre las 14:56:38 y las 15:08:31 UTC, una sesión abierta en otra
 * pestaña reescribió `token`: la pestaña vieja siguió pintando a `plachi`
 * (superadmin, con su foto) mientras **cada petición salía firmada como
 * `Ciocan`**, un `member`. Los 403 que se vieron eran correctos.
 *
 * El interceptor de `api.js` lee el token de `localStorage` en cada llamada, así
 * que en cuanto otra pestaña lo cambia, ésta empieza a firmar como otra persona
 * sin enterarse. Este módulo es el freno: en cuanto se detecta el cambio, toda
 * petición se rechaza aquí, antes de salir.
 *
 * **No se recarga en silencio, y es una decisión deliberada.** En un ERP una
 * recarga automática puede tirar un formulario a medio rellenar. Arreglar un
 * problema de integridad destruyendo trabajo del usuario no es un arreglo: se
 * bloquea, se avisa, y recarga la persona cuando haya guardado lo suyo.
 */

let bloqueada = false;
let motivo = null;
const suscriptores = new Set();

/** Error distinguible: la petición no salió, no es que el servidor la rechazara. */
export class SessionChangedError extends Error {
  constructor(razon) {
    super('La sesión cambió en otra pestaña. Recarga la página para continuar.');
    this.name = 'SessionChangedError';
    this.sessionChanged = true;
    this.reason = razon;
  }
}

export function lockSession(razon = 'token-changed') {
  if (bloqueada) return;
  bloqueada = true;
  motivo = razon;
  suscriptores.forEach((fn) => {
    try {
      fn(razon);
    } catch {
      /* un suscriptor roto no puede impedir que los demás se enteren */
    }
  });
}

export function isSessionLocked() {
  return bloqueada;
}

export function sessionLockReason() {
  return motivo;
}

export function onSessionLock(fn) {
  suscriptores.add(fn);
  return () => suscriptores.delete(fn);
}

/** Sólo para tests: ninguna ruta de producción desbloquea sin recargar. */
export function __resetSessionLock() {
  bloqueada = false;
  motivo = null;
  suscriptores.clear();
}
